/* eslint-disable react/jsx-no-constructed-context-values */
import React, { useContext, createContext, useCallback, useRef, useState } from 'react';
import apiCard from '../utils/apiCard';
import { buildAllYouCanEatGateKey } from '../utils/all-you-can-eat';
import { getCardAllYouCanEatEnabled } from '../services/allYouCanEatGate';
import { CardsResponse } from '../models/IDetailedBill';
import { IFinishCommandOrder } from '../models/IFinishCommandOrder';
import { useOrder } from './order';
import CallWaiterType from '../enum/CallWaiter';
import ISettings from '../models/ISettings';

export interface IPooling {
  hash: string;
  status: string;
  eventType: string;
}

interface ServiceCommandData {
  loadingAvailable: boolean;
  available: boolean | null;
  settings: ISettings | null;
  persistTableNumber: number;
  persistReceiverName: string;
  /**
   * Card or table number whose open launch the last availability check confirmed, or 0 when
   * nothing is open, the check failed or it has not run yet. A number rather than a flag, so a
   * consumer can tell an answer about the current QR from one about the previous QR.
   */
  openLaunchNumber: number;
  /**
   * Gate key (`buildAllYouCanEatGateKey`) whose last completed rodízio check answered "cleared", or
   * `null`. Kept here, above the routes, so going to the cart and back does not hide the rodízio again:
   * a consumer compares it with its own current key, which is also what keeps an answer about another
   * card or another table session from counting.
   */
  allYouCanEatKey: string | null;
  /**
   * Checks whether the card is cleared for rodízio. The endpoint receives only the card number; the
   * session revision only makes up the key. The previous answer is NOT cleared before asking: a
   * revalidation of the same key keeps the rodízio visible until the new answer arrives, and an answer
   * that was overtaken by a newer check is dropped.
   */
  checkAllYouCanEat: (
    baseURL: string | undefined,
    cardNumber: number,
    sessionRevision: number,
  ) => Promise<void>;
  /** Forgets the rodízio clearance and drops any check still in flight. */
  invalidateAllYouCanEat: () => void;
  callWaiter: (baseURL: string, tableNumber: number, serviceType: CallWaiterType) => Promise<void>;
  getAvailable: (baseURL: string, cardId: string) => Promise<void>;
  getSimpleSolutionSettings: (baseURL: string) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finishOrder: (payload: IFinishCommandOrder, baseUrl: string) => Promise<void>;
  getDetailedBill: (baseURL: string, tableNumber: number) => Promise<CardsResponse>;
}

const ServiceCommand = createContext<ServiceCommandData>({} as ServiceCommandData);

interface IServiceCommandProvider {
  children: React.ReactNode;
}

export const ServiceCommandProvider: React.FC<IServiceCommandProvider> = ({ children }) => {
  const { table } = useOrder();

  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [settings, setSettings] = useState<ISettings | null>(null);
  const [persistTableNumber, setPersistTableNumber] = useState<number>(0);
  const [persistReceiverName, setPersistReceiverName] = useState<string>('');
  const [openLaunchNumber, setOpenLaunchNumber] = useState<number>(0);
  const [allYouCanEatKey, setAllYouCanEatKey] = useState<string | null>(null);
  const allYouCanEatRequestRef = useRef(0);

  const checkAllYouCanEat = useCallback(
    async (baseURL: string | undefined, cardNumber: number, sessionRevision: number) => {
      allYouCanEatRequestRef.current += 1;
      const request = allYouCanEatRequestRef.current;

      const key = buildAllYouCanEatGateKey(baseURL, cardNumber, sessionRevision);

      if (key === null) {
        setAllYouCanEatKey(null);
        return;
      }

      // Never throws: any failure resolves false.
      const enabled = await getCardAllYouCanEatEnabled(baseURL, cardNumber);

      if (request !== allYouCanEatRequestRef.current) return;

      setAllYouCanEatKey(enabled ? key : null);
    },
    [],
  );

  const invalidateAllYouCanEat = useCallback(() => {
    allYouCanEatRequestRef.current += 1;
    setAllYouCanEatKey(null);
  }, []);

  const callWaiter = useCallback(
    async (baseURL: string, tableNumber: number, serviceType: CallWaiterType) => {
      const body = {
        tableNumber,
        serviceType,
      };
      const response = await apiCard.post('/waiters/request', body, { baseURL });
      return response.data;
    },
    [],
  );

  const getAvailable = useCallback(
    async (baseURL: string, cardId: string) => {
      // Cleared before asking, so a failed check never leaves the previous answer standing.
      setOpenLaunchNumber(0);

      try {
        setLoadingAvailable(true);
        const data = await apiCard.get(`/cards/${cardId}/available`, { baseURL });

        // if (data.data?.id) {
        //   const savedId = sessionStorage.getItem(SessionStorageKeys.ORDER_TABLE_ID);
        //   console.log('savedId', savedId);
        //   console.log('data.data?.id', data.data?.id);
        //   if (savedId) {
        //     if (Number(savedId) !== data.data?.id) {
        //       sessionStorage.removeItem('@BSFOOD:table_session');
        //       navigate('/table', { state: { expired: true } });
        //     }
        //   }
        // }
        if (table.isCard) {
          setPersistTableNumber(data?.data?.tableId || null);
        } else {
          setPersistTableNumber(0);
        }
        setPersistReceiverName(data?.data?.cardName || '');
        // 204 when nothing is open for that number; an open launch always carries its id.
        setOpenLaunchNumber(data?.data?.id > 0 ? Number(cardId) || 0 : 0);
        setAvailable(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setAvailable(false);
        throw error;
      } finally {
        setLoadingAvailable(false);
      }
    },
    [table],
  );

  const getSimpleSolutionSettings = useCallback(async (baseURL: string) => {
    try {
      setLoadingAvailable(true);
      const data = await apiCard.get('/settings', { baseURL });
      setSettings(data.data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setAvailable(false);
      throw error;
    } finally {
      setLoadingAvailable(false);
    }
  }, []);

  const getDetailedBill = useCallback(
    async (baseURL: string, tableNumber: number): Promise<CardsResponse> => {
      const { data } = await apiCard.get(
        `/ordersummaries/self-checkout/cards?cards=${tableNumber}`,
        { baseURL },
      );
      return data;
    },
    [],
  );

  const finishOrder = async (payload: IFinishCommandOrder, baseUrl: string): Promise<void> => {
    await apiCard.post('/orders/self-checkout/finish', payload, {
      baseURL: baseUrl,
    });
  };

  return (
    <ServiceCommand.Provider
      value={{
        settings,
        available,
        loadingAvailable,
        persistTableNumber,
        persistReceiverName,
        openLaunchNumber,
        allYouCanEatKey,
        checkAllYouCanEat,
        invalidateAllYouCanEat,
        callWaiter,
        finishOrder,
        getAvailable,
        getDetailedBill,
        getSimpleSolutionSettings,
      }}
    >
      {children}
    </ServiceCommand.Provider>
  );
};

export function useServiceCommand(): ServiceCommandData {
  const context = useContext(ServiceCommand);

  if (!context) {
    throw new Error('useServiceCommand must be used within ServiceCommandProvider');
  }

  return context;
}
